import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAdminState } from "../../hooks/admin/useAdminStateManager";
import apiClient from "../../services/apiClient";
import ItemSidebar from "./ItemSidebar";
import AssignmentContentArea from "./AssignmentContentArea";
import SaveCancelButtons from "./../SaveCancelButtons";
import LoadingState from "../LoadingState";
import { ErrorState } from "../ErrorState";
import UnsavedChangesModal from "../UnsavedChangesModal";
import { useApiState } from "../../hooks/shared/useApiState";
import { useFormState } from "../../hooks/shared/useFormState";
import { useAssignmentState } from "../../hooks/admin/useAssignmentState";
import { ENTITY_HANDLERS } from "../entityHandlers";

const AssignmentGenericManagement = ({
  items = [],
  assignableItems = [],
  userDetails = [],
  config,
  updateItemLocally,
  onShowPermissions,
  selectedGroupForPermissions,
  isPermissionsTabVisible,
  onRequestGroupChange,
  tab,
}) => {
  // Get state and actions from context
  const {
    selectedItems,
    isSaving: toolbarSaving,
    selectItem: setSelectedInContext,
    updateHasChanges,
    registerSaveHandler,
    unregisterSaveHandler,
  } = useAdminState();

  // Get the selected item for this tab from context
  const selectedItem = selectedItems[tab];

  const [searchTerm, setSearchTerm] = useState("");
  const [localSaving, setLocalSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Simple initialization state - just track what's been initialized
  const [initializedItemId, setInitializedItemId] = useState(null);

  const assignableItemsApi = useApiState();
  const entityHandler = ENTITY_HANDLERS[config.itemLabel];

  // Initialize form and assignment states
  const formState = useFormState(config, entityHandler, userDetails, items);
  const assignmentState = useAssignmentState(
    entityHandler,
    config,
    assignableItemsApi
  );

  // Calculate if there are any changes locally
  const hasLocalChanges = useMemo(() => {
    const assignmentsChanged = assignmentState.areAssignmentsChanged();
    const formChanged = formState.isFormDataChanged();
    return assignmentsChanged || formChanged;
  }, [assignmentState, formState]);

  // Update context when local changes state changes
  useEffect(() => {
    updateHasChanges(tab, hasLocalChanges);
  }, [hasLocalChanges, tab, updateHasChanges]);

  // Filtered items for search
  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    if (searchTerm === "*") return items;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return items?.filter((item) => {
      const lowerItemName = item.name.toLowerCase();

      if (lowerSearchTerm.startsWith("*") && lowerSearchTerm.endsWith("*")) {
        const searchMiddle = lowerSearchTerm.slice(1, -1);
        return lowerItemName.includes(searchMiddle);
      }

      if (lowerSearchTerm.startsWith("*") && !lowerSearchTerm.endsWith("*")) {
        const searchContent = lowerSearchTerm.slice(1);
        return lowerItemName.includes(searchContent);
      }

      if (lowerSearchTerm.endsWith("*")) {
        const searchPrefix = lowerSearchTerm.slice(0, -1);
        return lowerItemName.startsWith(searchPrefix);
      }

      return lowerItemName.startsWith(lowerSearchTerm);
    });
  }, [items, searchTerm]);

  // Simple initialization effect - runs when selectedItem changes
  useEffect(() => {
    if (!selectedItem) {
      // Clear everything when no item selected
      setInitializedItemId(null);
      formState.resetForm();
      assignmentState.resetAssignments();
      return;
    }

    // Skip if already initialized for this item
    if (selectedItem.id === initializedItemId) {
      return;
    }

    // Initialize the new item
    const initializeItem = async () => {
      formState.initializeFormData(selectedItem);
      try {
        await assignmentState.fetchAssignableItemsForItem(selectedItem.id);
        setInitializedItemId(selectedItem.id);
      } catch (error) {
        console.error("Error fetching assignments:", error);
        // Still mark as initialized to prevent infinite retries
        setInitializedItemId(selectedItem.id);
      }
    };

    initializeItem();
  }, [selectedItem?.id]); // Only depend on the ID, not the whole object

  // Simplified selectItem
  const selectItem = useCallback(
    (item) => {
      if (!item) return;
      const currentItem = items.find((i) => i.id === item.id) || item;
      setSelectedInContext(tab, currentItem);
    },
    [items, tab, setSelectedInContext]
  );

  // Handle item click with unsaved changes check
  const handleItemClick = useCallback(
    async (item) => {
      if (hasLocalChanges) {
        setPendingAction({ type: "itemClick", item });
        setShowUnsavedModal(true);
        return;
      }
      selectItem(item);
    },
    [hasLocalChanges, selectItem]
  );

  // Auto-select first item if needed
  useEffect(() => {
    if (items.length > 0 && !selectedItem) {
      const firstItem = items[0];
      if (firstItem?.id !== undefined && firstItem?.id !== null) {
        selectItem(firstItem);
      }
    }
  }, [items.length, !selectedItem]); // eslint-disable-line

  // Core save logic
  const performSave = useCallback(async () => {
    if (!selectedItem) return;

    if (!formState.validateForm(selectedItem)) {
      return;
    }

    const assignmentsChanged = assignmentState.areAssignmentsChanged();
    const formChanged = formState.isFormDataChanged();

    if (!assignmentsChanged && !formChanged) {
      return;
    }

    if (assignmentsChanged) {
      await assignmentState.saveAssignmentChanges(selectedItem, entityHandler);
    }

    if (formChanged) {
      const updateUrl = entityHandler.updateItemURL(selectedItem.id);
      const apiData = entityHandler.mapToApiFormat(
        formState.formData,
        selectedItem,
        userDetails,
        false
      );
      await apiClient.put(updateUrl, apiData);

      const updatedItem = updateItemLocally(
        entityHandler,
        formState.formData,
        selectedItem.id
      );

      if (updatedItem) {
        setSelectedInContext(tab, updatedItem);
        // Update tracking for the new item
        setInitializedItemId(updatedItem.id);
      }
    }

    assignmentState.markAssignmentsSaved();
    formState.markFormSaved();
  }, [
    selectedItem,
    formState,
    assignmentState,
    entityHandler,
    userDetails,
    updateItemLocally,
    tab,
    setSelectedInContext,
  ]);

  // Register this component's save handler with the parent context
  useEffect(() => {
    registerSaveHandler(tab, performSave);

    return () => {
      unregisterSaveHandler(tab);
    };
  }, [tab, performSave, registerSaveHandler, unregisterSaveHandler]);

  // Local save button handler
  const handleSave = useCallback(async () => {
    if (!hasLocalChanges) return;

    setLocalSaving(true);
    setSaveError(null);

    try {
      await performSave();
    } catch (error) {
      setSaveError(error.message || "Failed to save changes");
    } finally {
      setLocalSaving(false);
    }
  }, [performSave, hasLocalChanges]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (hasLocalChanges) {
      setPendingAction({ type: "cancel" });
      setShowUnsavedModal(true);
      return;
    }
    performCancel();
  }, [hasLocalChanges]);

  const performCancel = useCallback(() => {
    assignmentState.resetAssignments();
    formState.resetForm();
  }, [assignmentState, formState]);

  // Modal handlers
  const handleModalSave = async () => {
    if (pendingAction?.type === "cancel") {
      setPendingAction(null);
      setShowUnsavedModal(false);
      return;
    }

    try {
      await performSave();
      if (pendingAction?.type === "itemClick") {
        // Clear to force re-initialization
        setInitializedItemId(null);
        selectItem(pendingAction.item);
      }
    } catch (error) {
      setSaveError(error.message || "Failed to save changes");
      return;
    }

    setPendingAction(null);
    setShowUnsavedModal(false);
  };

  const handleModalDiscard = () => {
    if (pendingAction) {
      if (pendingAction.type === "itemClick") {
        assignmentState.resetAssignments();
        formState.resetForm();
        // Clear to force re-initialization
        setInitializedItemId(null);
        selectItem(pendingAction.item);
      } else if (pendingAction.type === "cancel") {
        performCancel();
      }
    }
    setPendingAction(null);
    setShowUnsavedModal(false);
  };

  const handleModalHide = () => {
    setPendingAction(null);
    setShowUnsavedModal(false);
  };

  // Handle permissions imported
  const handlePermissionsImported = useCallback(
    (importedGroupId) => {
      if (
        isPermissionsTabVisible &&
        selectedGroupForPermissions &&
        selectedGroupForPermissions.id === importedGroupId
      ) {
        if (onRequestGroupChange) {
          onRequestGroupChange(selectedGroupForPermissions);
        }
      }
    },
    [
      selectedGroupForPermissions?.id,
      isPermissionsTabVisible,
      onRequestGroupChange,
    ]
  );

  // Get modal props
  const getModalProps = () => {
    if (pendingAction?.type === "cancel") {
      return {
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        leftLabel: "Discard",
        rightLabel: "Cancel",
        onLeftAction: handleModalDiscard,
        onRightAction: handleModalHide,
      };
    }
    return {
      message:
        "You have unsaved changes. Do you want to save them before continuing?",
      leftLabel: "Save",
      rightLabel: "Discard",
      onLeftAction: handleModalSave,
      onRightAction: handleModalDiscard,
    };
  };

  // Error handling
  const allErrors = [assignableItemsApi.error, saveError].filter(Boolean);
  const hasErrors = allErrors.length > 0;
  const isContentLoading =
    !hasErrors && (assignableItemsApi.loading || !selectedItem);

  const clearAllErrors = () => {
    assignableItemsApi.clearError();
    setSaveError(null);
  };

  if (hasErrors) {
    return (
      <ErrorState error={allErrors.join(", ")} onClearError={clearAllErrors} />
    );
  }

  if (!items.length) {
    return (
      <LoadingState message={`Loading ${config.itemLabel.toLowerCase()}s...`} />
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 grid grid-cols-5 gap-4 p-6 min-h-0">
        <ItemSidebar
          items={filteredItems}
          selectedItem={selectedItem}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onItemClick={handleItemClick}
          itemLabel={config.itemLabel}
        />

        <div className="col-span-4 flex flex-col min-h-0 h-full">
          <AssignmentContentArea
            selectedItem={selectedItem}
            config={config}
            userDetails={userDetails}
            assignableItems={assignableItems}
            selectedAssignableItems={assignmentState.selectedAssignableItems}
            onToggleAssignableItem={assignmentState.toggleAssignableItem}
            formData={formState.formData}
            onFormChange={formState.setFormData}
            validationErrors={formState.validationErrors}
            isContentLoading={isContentLoading}
            onShowPermissions={onShowPermissions}
            groups={items}
            onPermissionsImported={handlePermissionsImported}
          />

          <SaveCancelButtons
            onSave={handleSave}
            onCancel={handleCancel}
            isSaving={localSaving && !toolbarSaving}
            disabled={!hasLocalChanges || toolbarSaving}
          />
        </div>
      </div>

      <UnsavedChangesModal
        show={showUnsavedModal}
        onHide={handleModalHide}
        onLeftAction={getModalProps().onLeftAction}
        onRightAction={getModalProps().onRightAction}
        message={getModalProps().message}
        leftLabel={getModalProps().leftLabel}
        rightLabel={getModalProps().rightLabel}
      />
    </div>
  );
};

export default AssignmentGenericManagement;
